import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost';

interface CommonProps {
  variant?: Variant;
  fullWidth?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonAsButton = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
    to?: undefined;
  };

type ButtonAsAnchor = CommonProps & {
  href: string;
  to?: undefined;
  target?: string;
  rel?: string;
  onClick?: () => void;
};

type ButtonAsLink = CommonProps & {
  to: string;
  href?: undefined;
  onClick?: () => void;
};

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsLink;

function classNames(variant: Variant, fullWidth?: boolean, className?: string) {
  return [
    styles.button,
    styles[variant],
    fullWidth ? styles.full : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    fullWidth,
    children,
    className,
    ...rest
  } = props;
  const cls = classNames(variant, fullWidth, className);

  if ('to' in props && props.to) {
    return (
      <Link to={props.to} className={cls} onClick={props.onClick}>
        {children}
      </Link>
    );
  }

  if ('href' in props && props.href) {
    return (
      <a
        href={props.href}
        className={cls}
        target={props.target}
        rel={props.rel}
        onClick={props.onClick}
      >
        {children}
      </a>
    );
  }

  const buttonProps = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type={buttonProps.type ?? 'button'} className={cls} {...buttonProps}>
      {children}
    </button>
  );
}
